import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import MascotBand from "../components/MascotBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Translation-ready content (FR-first, EN secondary) ───
const T = {
  fr: {
    badge: "BIENVENUE · KAIZEN SYSTEM",
    title: "Choisis ton parcours",
    subtitle: "Cette plateforme s'adapte à toi. Sélectionne ton rôle pour découvrir tes outils essentiels en moins de 3 minutes.",
    chooseRole: "Quel est ton rôle ?",
    startHere: "Commence ici",
    helpTitle: "Besoin d'aide ?",
    helpMsg: "Écris à ralph646@hotmail.com ou appelle au (819) 527-9446.",
    languageToggle: "EN",
    backHome: "← Accueil",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
    nextStep: "Étape suivante",
    completeOnboarding: "C'est compris, on y va !",
    videoPlaceholder: "📹 Capsule vidéo d'introduction (à venir)",
    videoSubtitle: "Une vidéo de 90 secondes te guidera dans la plateforme.",
    welcomeKru: "Salut, c'est Kru Ralph 🥋",
    welcomeKruMsg: "Bienvenue dans le système KAIZEN — la plateforme construite par Institut Impact Élite Martial pour les artistes martiaux, leurs familles, mes partenaires et mes sponsors.",
  },
  en: {
    badge: "WELCOME · KAIZEN SYSTEM",
    title: "Choose your path",
    subtitle: "This platform adapts to you. Pick your role to discover your essential tools in under 3 minutes.",
    chooseRole: "What's your role?",
    startHere: "Start here",
    helpTitle: "Need help?",
    helpMsg: "Email ralph646@hotmail.com or call (819) 527-9446.",
    languageToggle: "FR",
    backHome: "← Home",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
    nextStep: "Next step",
    completeOnboarding: "Got it, let's go!",
    videoPlaceholder: "📹 Intro video capsule (coming soon)",
    videoSubtitle: "A 90-second video will guide you through the platform.",
    welcomeKru: "Hi, I'm Kru Ralph 🥋",
    welcomeKruMsg: "Welcome to the KAIZEN system — the platform built by Institut Impact Élite Martial for martial artists, their families, my partners and sponsors.",
  },
};

// ─── Roles + their start-here cards ───
const ROLES = [
  {
    id: "parent",
    emoji: "👨‍👩‍👧",
    label_fr: "Parent",
    label_en: "Parent",
    desc_fr: "Tu accompagnes un enfant dans son parcours martial.",
    desc_en: "You accompany a child on their martial journey.",
    cards: [
      { fr_title: "Suivre la progression", fr_body: "Consulte les ceintures, attendance et compétences de ton enfant en temps réel.", en_title: "Track progression", en_body: "View belts, attendance and skills of your child in real time.", action_label_fr: "Mon portail", action_label_en: "My portal", action_link: "/portail-client" },
      { fr_title: "Inscrire à une Mini Compétition", fr_body: "Mini Comp KAIZEN Automne 7 nov 2026 — inscription en ligne sécurisée.", en_title: "Register for a Mini Tournament", en_body: "KAIZEN Mini Comp Fall Nov 7, 2026 — secure online registration.", action_label_fr: "Voir les comps", action_label_en: "View tournaments", action_link: "/" },
      { fr_title: "Acheter des billets", fr_body: "Billets familles, places réservées ou tables VIP pour les événements.", en_title: "Buy tickets", en_body: "Family tickets, reserved seats or VIP tables for events.", action_label_fr: "Billetterie", action_label_en: "Ticketing", action_link: "/billetterie/mini-competition-kaizen-automne-2026" },
      { fr_title: "Paiements mensuels", fr_body: "Gestion sécurisée via Stripe — pas besoin de chèques ni d'argent comptant.", en_title: "Monthly payments", en_body: "Secure management via Stripe — no need for checks or cash.", action_label_fr: null },
    ],
  },
  {
    id: "student",
    emoji: "🥋",
    label_fr: "Élève",
    label_en: "Student",
    desc_fr: "Tu pratiques au dojo. Voici ton tableau de bord.",
    desc_en: "You train at the dojo. Here's your dashboard.",
    cards: [
      { fr_title: "Ton portail élève", fr_body: "Calendrier des cours, attendance, badges, progression technique.", en_title: "Your student portal", en_body: "Class calendar, attendance, badges, technical progression.", action_label_fr: "Mon portail", action_label_en: "My portal", action_link: "/portail-client" },
      { fr_title: "Compétitions à venir", fr_body: "Mini Compétitions internes + circuits provinciaux. Inscriptions ouvertes.", en_title: "Upcoming competitions", en_body: "Internal Mini Comps + provincial circuits. Open registrations.", action_label_fr: "Voir", action_label_en: "View", action_link: "/" },
      { fr_title: "Boutique KAIZEN", fr_body: "Vêtements, équipements, sceau de réussite. Livraison à ton dojo.", en_title: "KAIZEN Store", en_body: "Apparel, gear, achievement seals. Delivered to your dojo.", action_label_fr: "Boutique", action_label_en: "Store", action_link: "/boutique" },
    ],
  },
  {
    id: "coach",
    emoji: "🎓",
    label_fr: "Coach",
    label_en: "Coach",
    desc_fr: "Tu enseignes, suis tes élèves et coordonnes les événements.",
    desc_en: "You teach, track your students and coordinate events.",
    cards: [
      { fr_title: "Gestion CRM élèves", fr_body: "Liste complète, attendance, progression, alertes retention.", en_title: "Student CRM", en_body: "Full list, attendance, progression, retention alerts.", action_label_fr: "CRM", action_label_en: "CRM", action_link: "/app/eleves" },
      { fr_title: "Mini Compétitions", fr_body: "Crée, gère brackets, lance kit marketing automatisé.", en_title: "Mini Tournaments", en_body: "Create, manage brackets, launch automated marketing kit.", action_label_fr: "Module", action_label_en: "Module", action_link: "/app/mini-competitions" },
      { fr_title: "Debriefs anonymes", fr_body: "Récolte feedback post-événement anonyme de tes équipes.", en_title: "Anonymous debriefs", en_body: "Collect anonymous post-event feedback from your teams.", action_label_fr: "Debriefs", action_label_en: "Debriefs", action_link: "/app/debriefs" },
    ],
  },
  {
    id: "admin",
    emoji: "⚙️",
    label_fr: "Admin / Kru",
    label_en: "Admin / Master",
    desc_fr: "Tu gères toute la plateforme : élèves, finances, sponsors, événements.",
    desc_en: "You manage the entire platform: students, finances, sponsors, events.",
    cards: [
      { fr_title: "AI Operator 24/7", fr_body: "Ton chef de cabinet IA : brief quotidien, alertes, analyses.", en_title: "AI Operator 24/7", en_body: "Your AI chief of staff: daily brief, alerts, insights.", action_label_fr: "Lancer", action_label_en: "Launch", action_link: "/app/operator" },
      { fr_title: "Tableau de bord", fr_body: "KPIs globaux, revenus, attendance, alertes business.", en_title: "Dashboard", en_body: "Global KPIs, revenue, attendance, business alerts.", action_label_fr: "Dashboard", action_label_en: "Dashboard", action_link: "/app" },
      { fr_title: "Billetterie + Livestream", fr_body: "Configure événements, sources vidéo, accès sponsor/media.", en_title: "Ticketing + Livestream", en_body: "Configure events, video sources, sponsor/media access.", action_label_fr: "Billetterie", action_label_en: "Ticketing", action_link: "/app/billetterie" },
      { fr_title: "Sponsors & ROI", fr_body: "Pipeline commandites, livraisons, rapports ROI copyables.", en_title: "Sponsors & ROI", en_body: "Sponsorship pipeline, deliverables, copyable ROI reports.", action_label_fr: "Sponsors", action_label_en: "Sponsors", action_link: "/app/commandites" },
    ],
  },
  {
    id: "private_client",
    emoji: "💎",
    label_fr: "Client privé",
    label_en: "Private client",
    desc_fr: "Tu suis un coaching privé avec Kru Ralph.",
    desc_en: "You're in private coaching with Kru Ralph.",
    cards: [
      { fr_title: "Ton portail privé", fr_body: "Programme personnalisé, sessions filmées, notes de progression.", en_title: "Your private portal", en_body: "Personalized program, recorded sessions, progress notes.", action_label_fr: "Portail", action_label_en: "Portal", action_link: "/portail-client" },
      { fr_title: "Planifier une session", fr_body: "Calendrier privé Kru — disponibilités en temps réel.", en_title: "Schedule a session", en_body: "Kru's private calendar — real-time availability.", action_label_fr: null },
      { fr_title: "Paiements & abonnement", fr_body: "Facturation mensuelle Stripe sécurisée + reçus auto.", en_title: "Payments & subscription", en_body: "Secure monthly Stripe billing + automatic receipts.", action_label_fr: null },
    ],
  },
  {
    id: "sponsor",
    emoji: "🏆",
    label_fr: "Sponsor / Partenaire",
    label_en: "Sponsor / Partner",
    desc_fr: "Tu commandites Institut Impact Élite Martial ou un événement KAIZEN.",
    desc_en: "You sponsor Institut Impact Élite Martial or a KAIZEN event.",
    cards: [
      { fr_title: "Tes engagements", fr_body: "Liste des avantages convenus (logo, mentions, livestream, mentions).", en_title: "Your commitments", en_body: "List of agreed benefits (logo, mentions, livestream, callouts).", action_label_fr: null },
      { fr_title: "Rapport ROI", fr_body: "Stats événement, reach social, livraisons documentées.", en_title: "ROI Report", en_body: "Event stats, social reach, documented deliverables.", action_label_fr: null },
      { fr_title: "Accès livestream gratuit", fr_body: "Lien personnel pour assister en direct + replay 30j.", en_title: "Free livestream access", en_body: "Personal link for live + 30-day replay.", action_label_fr: null },
    ],
  },
];

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role");
  const initialLang = searchParams.get("lang") === "en" ? "en" : "fr";
  const utmSource = searchParams.get("source") || searchParams.get("utm_source") || null;

  const [lang, setLang] = useState(initialLang);
  const [selectedRoleId, setSelectedRoleId] = useState(
    ROLES.find((r) => r.id === initialRole) ? initialRole : null
  );
  const t = T[lang];
  const selectedRole = ROLES.find((r) => r.id === selectedRoleId);

  // Track visit analytics (fire-and-forget, no auth required)
  useEffect(() => {
    axios.post(`${API}/onboarding/track`, {
      role: initialRole || null,
      lang,
      source: utmSource,
      referrer: document.referrer || null,
      path: window.location.pathname + window.location.search,
    }).catch(() => {});
    // Run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="onboarding-page">
      {/* Top bar */}
      <div className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xs tracking-[0.3em] text-amber-400 hover:text-amber-300">KAIZEN SYSTEM</Link>
          <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs border border-zinc-700 px-3 py-1.5 rounded-full hover:border-amber-500 hover:text-amber-400 transition" data-testid="lang-toggle-btn">
            {t.languageToggle}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Welcome hero */}
        {!selectedRole && (
          <>
            <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              {t.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="onboarding-title">
              {t.title}
            </h1>
            <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">{t.subtitle}</p>

            {/* Kru welcome card */}
            <div className="mt-8 border border-amber-500/30 rounded-2xl p-5 bg-gradient-to-br from-amber-500/10 to-amber-500/5" data-testid="kru-welcome">
              <div className="text-sm font-bold text-amber-300">{t.welcomeKru}</div>
              <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{t.welcomeKruMsg}</p>
            </div>

            {/* Video placeholder */}
            <div className="mt-6 border border-zinc-800 rounded-2xl p-8 bg-zinc-950/40 text-center" data-testid="video-placeholder">
              <div className="text-3xl mb-2">{t.videoPlaceholder.split(" ")[0]}</div>
              <div className="text-sm text-zinc-300 font-semibold">{t.videoPlaceholder.split(" ").slice(1).join(" ")}</div>
              <div className="text-xs text-zinc-500 mt-2">{t.videoSubtitle}</div>
            </div>

            {/* Roles grid */}
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-4">{t.chooseRole.toUpperCase()}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="roles-grid">
                {ROLES.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRoleId(r.id)}
                    className="text-left border border-zinc-800 rounded-2xl p-5 bg-zinc-950/50 hover:border-amber-500/50 hover:bg-amber-500/5 transition"
                    data-testid={`role-${r.id}`}
                  >
                    <div className="text-3xl mb-2">{r.emoji}</div>
                    <div className="text-lg font-bold">{lang === "fr" ? r.label_fr : r.label_en}</div>
                    <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{lang === "fr" ? r.desc_fr : r.desc_en}</div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="mt-12">
          <MascotBand
            title="Bienvenue dans la famille KAIZEN"
            subtitle="KAI, MEI et LANA t'accompagnent dès ton premier pas à l'Institut Impact Élite Martial — du tout premier cours jusqu'à ta meilleure version."
          />
        </div>

        {/* Selected role detail */}
        {selectedRole && (
          <>
            <button onClick={() => setSelectedRoleId(null)} className="text-xs text-zinc-400 hover:text-white mb-6" data-testid="back-to-roles-btn">← {lang === "fr" ? "Changer de rôle" : "Change role"}</button>
            <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">{t.startHere.toUpperCase()}</div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="role-title">
              {selectedRole.emoji} {lang === "fr" ? selectedRole.label_fr : selectedRole.label_en}
            </h1>
            <p className="mt-3 text-zinc-400">{lang === "fr" ? selectedRole.desc_fr : selectedRole.desc_en}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="start-here-cards">
              {selectedRole.cards.map((c, i) => {
                const title = lang === "fr" ? c.fr_title : c.en_title;
                const body = lang === "fr" ? c.fr_body : c.en_body;
                const actionLabel = lang === "fr" ? c.action_label_fr : c.action_label_en;
                return (
                  <div key={i} className="border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40" data-testid={`card-${selectedRole.id}-${i}`}>
                    <div className="text-base font-bold text-zinc-100">{title}</div>
                    <div className="text-sm text-zinc-400 mt-2 leading-relaxed">{body}</div>
                    {actionLabel && c.action_link && (
                      <Link to={c.action_link} className="mt-3 inline-block text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-3 py-1.5">
                        {actionLabel} →
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between border-t border-zinc-800 pt-6 flex-wrap gap-3">
              <Link to="/" className="text-sm text-zinc-400 hover:text-white">{t.backHome}</Link>
              <Link to={selectedRole.id === "admin" ? "/app" : (selectedRole.id === "parent" || selectedRole.id === "student" || selectedRole.id === "private_client" ? "/portail-client" : "/")} className="text-sm bg-amber-500 hover:bg-amber-400 text-black font-semibold px-5 py-2.5 rounded-full" data-testid="complete-onboarding-btn">
                {t.completeOnboarding} →
              </Link>
            </div>
          </>
        )}

        {/* Persistent help block */}
        <div className="mt-12 border border-zinc-800 rounded-2xl p-5 bg-zinc-950/40" data-testid="help-block">
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">⚙️ {t.helpTitle.toUpperCase()}</div>
          <div className="text-sm text-zinc-300">{t.helpMsg}</div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}
